import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';

type ToastVariant = 'success' | 'error' | 'warning' | 'info';

interface ToastItem {
  id: number;
  variant: ToastVariant;
  message: React.ReactNode;
}

interface ToastContextValue {
  showToast: (message: React.ReactNode, variant?: ToastVariant, durationMs?: number) => void;
  success: (message: React.ReactNode) => void;
  error: (message: React.ReactNode) => void;
  warning: (message: React.ReactNode) => void;
  info: (message: React.ReactNode) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const slideIn = keyframes`
  from { transform: translateX(1rem); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const Stack = styled.div`
  position: fixed;
  top: var(--space-lg);
  right: var(--space-lg);
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  z-index: 1100;
  max-width: 24rem;
  width: calc(100% - 2 * var(--space-lg));
`;

const variantStyles = {
  success: css`
    border-left-color: var(--success);
    svg.toast-icon { color: var(--success); }
  `,
  error: css`
    border-left-color: var(--error);
    svg.toast-icon { color: var(--error); }
  `,
  warning: css`
    border-left-color: var(--warning);
    svg.toast-icon { color: var(--warning); }
  `,
  info: css`
    border-left-color: var(--info);
    svg.toast-icon { color: var(--info); }
  `,
};

const Item = styled.div<{ $variant: ToastVariant }>`
  display: flex;
  align-items: flex-start;
  gap: var(--space-sm);
  background: var(--card-bg);
  border: 1px solid var(--gray-200);
  border-left: 3px solid;
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-lg);
  padding: var(--space-md);
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  animation: ${slideIn} 0.2s ease-out;

  ${({ $variant }: { $variant: ToastVariant }) => variantStyles[$variant]}

  svg.toast-icon {
    width: 1.125rem;
    height: 1.125rem;
    flex-shrink: 0;
    margin-top: 0.0625rem;
  }
`;

const Message = styled.div`
  flex: 1;
`;

const Dismiss = styled.button`
  display: flex;
  background: none;
  border: none;
  padding: 0;
  color: var(--gray-400);
  cursor: pointer;

  &:hover {
    color: var(--gray-700);
  }

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const icons: Record<ToastVariant, React.ReactNode> = {
  success: <CheckCircle2 className="toast-icon" />,
  error: <AlertCircle className="toast-icon" />,
  warning: <AlertTriangle className="toast-icon" />,
  info: <Info className="toast-icon" />,
};

/**
 * Provider delle notifiche toast. Uso:
 *   const toast = useToast();
 *   toast.success('Salvataggio completato');
 */
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (message: React.ReactNode, variant: ToastVariant = 'info', durationMs = 4000) => {
      const id = nextId.current++;
      setToasts((current) => [...current, { id, variant, message }]);
      if (durationMs > 0) {
        window.setTimeout(() => dismiss(id), durationMs);
      }
    },
    [dismiss]
  );

  const value: ToastContextValue = {
    showToast,
    success: (m) => showToast(m, 'success'),
    error: (m) => showToast(m, 'error', 6000),
    warning: (m) => showToast(m, 'warning'),
    info: (m) => showToast(m, 'info'),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Stack>
        {toasts.map((t) => (
          <Item key={t.id} $variant={t.variant} role="status">
            {icons[t.variant]}
            <Message>{t.message}</Message>
            <Dismiss onClick={() => dismiss(t.id)} aria-label="Chiudi notifica">
              <X />
            </Dismiss>
          </Item>
        ))}
      </Stack>
    </ToastContext.Provider>
  );
};

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast deve essere usato dentro <ToastProvider>');
  }
  return ctx;
}
